import { useMemo, useRef } from 'react';
import { IProduct, IProductSelectorTree } from '@topshots/core-models';
import dagre from '@dagrejs/dagre';
import { sortOptions } from '../selector-editor-utils';
import {
  QuestionNodeData,
  ProductLeafNodeData,
  SelectorFlowNode,
  SelectorFlowEdge,
} from './canvas-types';

const QUESTION_NODE_WIDTH = 240;
const QUESTION_NODE_BASE_HEIGHT = 52;
const QUESTION_NODE_OPTION_HEIGHT = 28;
const PRODUCT_NODE_WIDTH = 200;
const PRODUCT_NODE_HEIGHT = 60;

function getQuestionNodeHeight(optionCount: number): number {
  return QUESTION_NODE_BASE_HEIGHT + optionCount * QUESTION_NODE_OPTION_HEIGHT;
}

function treeToFlow(
  tree: IProductSelectorTree,
  productsById: Map<string, IProduct>,
  pinnedProductIds?: Set<string>
): { nodes: SelectorFlowNode[]; edges: SelectorFlowEdge[] } {
  const nodes: SelectorFlowNode[] = [];
  const edges: SelectorFlowEdge[] = [];
  const referencedProducts = new Set<string>();

  for (const node of Object.values(tree.nodes)) {
    const options = sortOptions(node.options);

    nodes.push({
      id: node.id,
      type: 'question',
      position: { x: 0, y: 0 },
      data: {
        prompt: node.prompt,
        options,
        isRoot: node.id === tree.rootNodeId,
      } as QuestionNodeData,
    });

    for (const option of options) {
      if (option.next.type === 'node') {
        edges.push({
          id: `${node.id}-${option.id}`,
          source: node.id,
          sourceHandle: option.id,
          target: option.next.nodeId,
          label: option.label,
        });
      } else if (option.next.type === 'product') {
        const productNodeId = `product_${option.next.productId}`;
        referencedProducts.add(option.next.productId);
        edges.push({
          id: `${node.id}-${option.id}`,
          source: node.id,
          sourceHandle: option.id,
          target: productNodeId,
          label: option.label,
        });
      }
      // type === 'none': no edge — option is unconnected
    }
  }

  // Include pinned products that aren't already referenced
  const allProductIds = new Set(referencedProducts);
  if (pinnedProductIds) {
    for (const id of pinnedProductIds) {
      allProductIds.add(id);
    }
  }

  for (const productId of allProductIds) {
    const product = productsById.get(productId);
    nodes.push({
      id: `product_${productId}`,
      type: 'productLeaf',
      position: { x: 0, y: 0 },
      data: {
        productId,
        name: product?.name ?? 'Unknown Product',
        price: product?.price ?? 0,
      } as ProductLeafNodeData,
    });
  }

  return { nodes, edges };
}

function applyDagreLayout(
  nodes: SelectorFlowNode[],
  edges: SelectorFlowEdge[]
): SelectorFlowNode[] {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: 'TB',
    nodesep: 80,
    ranksep: 120,
  });
  g.setDefaultEdgeLabel(() => ({}));

  for (const node of nodes) {
    const width =
      node.type === 'question' ? QUESTION_NODE_WIDTH : PRODUCT_NODE_WIDTH;
    const height =
      node.type === 'question'
        ? getQuestionNodeHeight(
            (node.data as QuestionNodeData).options.length
          )
        : PRODUCT_NODE_HEIGHT;

    g.setNode(node.id, { width, height });
  }

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  return nodes.map((node) => {
    const dagreNode = g.node(node.id);
    if (!dagreNode) return node;

    return {
      ...node,
      position: {
        x: dagreNode.x - dagreNode.width / 2,
        y: dagreNode.y - dagreNode.height / 2,
      },
    };
  });
}

/**
 * Computes a structural fingerprint of the tree for detecting when re-layout is needed.
 * Text-only changes (prompt, label edits) do not change the fingerprint.
 */
function getStructuralFingerprint(tree: IProductSelectorTree): string {
  const parts: string[] = [tree.rootNodeId];
  const sortedNodeIds = Object.keys(tree.nodes).sort();

  for (const nodeId of sortedNodeIds) {
    const node = tree.nodes[nodeId];
    parts.push(nodeId);
    parts.push(String(node.options.length));
    for (const option of sortOptions(node.options)) {
      parts.push(option.id);
      let nextKey = 'none';
      if (option.next.type === 'node') {
        nextKey = `n:${option.next.nodeId}`;
      } else if (option.next.type === 'product') {
        nextKey = `p:${option.next.productId}`;
      }
      parts.push(nextKey);
    }
  }

  return parts.join('|');
}

export function useCanvasLayout(
  tree: IProductSelectorTree | undefined,
  productsById: Map<string, IProduct>,
  pinnedProductIds?: Set<string>
) {
  const lastFingerprintRef = useRef<string>('');
  const lastLayoutRef = useRef<SelectorFlowNode[]>([]);

  return useMemo(() => {
    if (!tree) {
      return { nodes: [] as SelectorFlowNode[], edges: [] as SelectorFlowEdge[] };
    }

    const { nodes, edges } = treeToFlow(tree, productsById, pinnedProductIds);
    const pinnedSuffix = pinnedProductIds?.size
      ? `|pinned:${[...pinnedProductIds].sort().join(',')}`
      : '';
    const fingerprint = getStructuralFingerprint(tree) + pinnedSuffix;

    let layoutNodes: SelectorFlowNode[];
    if (fingerprint !== lastFingerprintRef.current) {
      layoutNodes = applyDagreLayout(nodes, edges);
      lastFingerprintRef.current = fingerprint;
      lastLayoutRef.current = layoutNodes;
    } else {
      // Structure unchanged (text-only edit) — preserve positions from last layout
      const positionMap = new Map(
        lastLayoutRef.current.map((n) => [n.id, n.position])
      );
      layoutNodes = nodes.map((node) => ({
        ...node,
        position: positionMap.get(node.id) ?? node.position,
      }));
      lastLayoutRef.current = layoutNodes;
    }

    return { nodes: layoutNodes, edges };
  }, [tree, productsById, pinnedProductIds]);
}
